// FRONTEND src/components/About.jsx
// Drift Team Website 2025
//
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 22.04.2025 to use AchievementsContext and prevent excessive API calls
//

import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import SectionTitle from '../common/SectionTitle';
import { getUserLanguage } from '../../utils/language';
import { ENABLE_MOCK_API } from '../../config';
import { useAchievements } from '../../contexts/AchievementsContext';

const About = () => {
  const { stats, loading } = useAchievements();
  const [localStats, setLocalStats] = useState([]);
  const language = getUserLanguage();

  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // Dynamické texty podle jazyka
  const texts = {
    cs: {
      badge: 'O nás',
      title: 'Kdo jsme',
      highlightedText: 'NO RISK NO DRIFT',
      description: 'Jsme profesionální drift tým s vášní pro motosport a kontrolovaný smyk. Spojujeme adrenalin, preciznost a diváckou show.',
      storyTitle: 'Náš příběh',
      storyText1: 'NO RISK NO DRIFT tým byl založen v roce 2017 skupinou nadšenců do driftového sportu v České Lípě. Z malé skupiny přátel jsme se postupně vypracovali na profesionální závodní tým, který se účastní národních i mezinárodních soutěží.',
      storyText2: 'Naší filozofií je "No Risk, No Drift" - musíte riskovat, abyste mohli driftovat. Tento přístup se odráží v našem stylu jízdy i přístupu k fanouškům a partnerům. Neustále posouváme hranice možností a zlepšujeme své dovednosti.',
      storyText3: 'Dnes jsme hrdým týmem s pěti tituly mistra ČR v driftingu a řadou úspěchů v evropských soutěžích. Kromě závodění se také věnujeme exhibicím, výuce driftu a poskytování adrenalinových zážitků pro veřejnost.',
      missionTitle: 'Naše mise',
      missionText: 'Chceme přinášet radost z driftingu široké veřejnosti, popularizovat tento dynamický motorsport v České republice a vychovávat novou generaci drifterů. Zároveň usilujeme o profesionální úspěchy na mezinárodní úrovni a reprezentaci českého motorsportu v zahraničí.',
      buttonText: 'Poznejte náš tým',
      mockStats: [
        { id: 1, title: 'Titulů mistra ČR', value: 5 },
        { id: 2, title: 'Závodů a exhibicí', value: 100 },
        { id: 3, title: 'Spokojených zákazníků', value: 1000 },
        { id: 4, title: 'Členů týmu', value: 8 },
      ],
    },
    en: {
      badge: 'About Us',
      title: 'Who We Are',
      highlightedText: 'NO RISK NO DRIFT',
      description: 'We are a professional drift team passionate about motorsport and controlled skids. We combine adrenaline, precision, and a spectator show.',
      storyTitle: 'Our Story',
      storyText1: 'The NO RISK NO DRIFT team was founded in 2017 by a group of drifting enthusiasts in Česká Lípa. From a small group of friends, we gradually grew into a professional racing team competing in national and international events.',
      storyText2: 'Our philosophy is "No Risk, No Drift" – you have to take risks to drift. This approach is reflected in our driving style and our interactions with fans and partners. We constantly push the boundaries of what’s possible and improve our skills.',
      storyText3: 'Today, we are a proud team with five Czech Republic drifting titles and numerous successes in European competitions. Beyond racing, we also focus on exhibitions, drift training, and providing adrenaline experiences for the public.',
      missionTitle: 'Our Mission',
      missionText: 'We aim to bring the joy of drifting to a wide audience, popularize this dynamic motorsport in the Czech Republic, and train the next generation of drifters. At the same time, we strive for professional success internationally and to represent Czech motorsport abroad.',
      buttonText: 'Meet Our Team',
      mockStats: [
        { id: 1, title: 'Czech Championship Titles', value: 5 },
        { id: 2, title: 'Races and Exhibitions', value: 100 },
        { id: 3, title: 'Satisfied Customers', value: 1000 },
        { id: 4, title: 'Team Members', value: 8 },
      ],
    },
  };

  const currentText = texts[language] || texts.en; // Fallback na angličtinu

  useEffect(() => {
    if (ENABLE_MOCK_API || !stats.length) {
      setLocalStats(currentText.mockStats);
    } else {
      setLocalStats(stats);
    }
  }, [stats, currentText, language]);

  if (loading) {
    return (
      <section id="about" className="diagonal-section">
        <div className="container mx-auto px-4 sm:px-6">
          <SectionTitle
            badge={currentText.badge}
            title={currentText.title}
            highlightedText={currentText.highlightedText}
            description={language === 'cs' ? 'Načítání...' : 'Loading...'}
          />
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="about" className="diagonal-section" ref={sectionRef}>
      <div className="container mx-auto px-4 sm:px-6">
        <SectionTitle
          badge={currentText.badge}
          title={currentText.title}
          highlightedText={currentText.highlightedText}
          description={currentText.description}
        />

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 about-content ${inView ? 'animate-fade-in' : 'opacity-0'}`}>
          <div className="about-card">
            <h3 className="text-2xl font-title font-bold text-white mb-4 red-accent pl-4">{currentText.storyTitle}</h3>
            <p className="text-gray-300 mb-4">{currentText.storyText1}</p>
            <p className="text-gray-300 mb-4">{currentText.storyText2}</p>
            <p className="text-gray-300">{currentText.storyText3}</p>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {localStats.map((stat, index) => (
                <div
                  key={stat.id}
                  className={`about-stat ${inView ? 'count-up' : 'opacity-0'}`}
                  style={{ animationDelay: `${0.2 * (index + 1)}s` }}
                >
                  <p className="stat-number text-red-500">{stat.value}+</p>
                  <p className="text-white font-medium">{stat.translations?.[language]?.title || stat.title}</p>
                </div>
              ))}
            </div>

            <div className="about-card mt-6">
              <h3 className="text-2xl font-title font-bold text-white mb-4 red-accent pl-4">{currentText.missionTitle}</h3>
              <p className="text-gray-300">{currentText.missionText}</p>
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <a href="#team" className="btn-drift">
            <i className="fas fa-users text-xs mr-2"></i>
            {currentText.buttonText}
          </a>
        </div>
      </div>
    </section>
  );
};

export default About;